import React, { useState, useEffect } from "react";
import { Copy, RefreshCw, ThumbsUp, ThumbsDown, Check } from "lucide-react";
import { ChatMessageProps } from "../Types/Types";
import { ActionButton } from "../../Common";
import ReactMarkdown from "react-markdown";

export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  isLastMessage,
  onRegenerate,
}) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const [feedback, setFeedback] = useState<"up" | "down" | null>(null);
  const [displayedContent, setDisplayedContent] = useState("");
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    if (message.sender === "ai") {
      setIsTyping(true);
      setDisplayedContent("");

      let currentIndex = 0;
      const typingSpeed = 15;
      let timeoutId: NodeJS.Timeout;
      const typeText = () => {
        if (currentIndex < message.content.length) {
          setDisplayedContent(message.content.slice(0, currentIndex + 1));
          currentIndex++;
          let nextDelay = typingSpeed;
          const currentChar = message.content[currentIndex - 1];
          if (
            currentChar === "." ||
            currentChar === "!" ||
            currentChar === "?"
          ) {
            nextDelay = typingSpeed * 3;
          } else if (currentChar === "," || currentChar === ";") {
            nextDelay = typingSpeed * 2;
          } else if (currentChar === " ") {
            nextDelay = typingSpeed * 0.8;
          }
          timeoutId = setTimeout(typeText, nextDelay);
        } else {
          setIsTyping(false);
        }
      };
      const startDelay = setTimeout(typeText, 50);

      return () => {
        clearTimeout(startDelay);
        setIsTyping(false);
      };
    } else {
      setDisplayedContent(message.content);
      setIsTyping(false);
    }
  }, [message.content, message.sender]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
      const textArea = document.createElement("textarea");
      textArea.value = message.content;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (fallbackErr) {
        console.error("Fallback copy failed:", fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleFeedback = (type: "up" | "down") => {
    setFeedback(feedback === type ? null : type);
    console.log(
      `Feedback: ${type} for message: ${message.content.substring(0, 50)}...`,
    );
  };

  return (
    <div className="flex w-full flex-col gap-1.5">
      <div
        className={`flex items-center gap-1.5 ${
          message.sender === "user" ? "justify-end" : "justify-start"
        }`}
      >
        {message.sender === "ai" ? (
          <div className="flex items-center gap-1.5">
            <div className="h-4 w-4">
              <svg
                width="20"
                height="21"
                viewBox="0 0 20 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19.4885 8.62331C19.2967 8.76697 19.1229 8.95037 18.9121 9.04849C17.9265 9.50987 16.9374 9.96307 15.9383 10.3918C14.8438 10.8613 14.0892 11.6533 13.6418 12.7933C13.2269 13.8504 12.775 14.8923 12.3152 15.9296C12.2333 16.1129 12.0326 16.2403 11.8869 16.3932C11.7557 16.2449 11.5785 16.1187 11.5022 15.9447C11.0156 14.8351 10.5469 13.7172 10.0826 12.5971C9.67109 11.6065 8.99378 10.9022 8.03952 10.4747C6.96192 9.99111 5.8843 9.5052 4.81566 9.00059C4.64745 8.92116 4.52634 8.73778 4.38281 8.60228C4.52971 8.45044 4.65194 8.24135 4.82799 8.15608C5.84392 7.67133 6.8666 7.20061 7.90047 6.75674C8.97809 6.29418 9.71706 5.51042 10.1622 4.39492C10.5906 3.32263 11.0447 2.26087 11.5101 1.20494C11.5841 1.03791 11.7758 0.813636 11.907 0.818309C12.0472 0.822981 12.2322 1.04374 12.3051 1.21311C12.7716 2.29007 13.2179 3.3752 13.6608 4.46266C14.0724 5.47537 14.7553 6.20075 15.7263 6.63994C16.786 7.11884 17.8434 7.60359 18.8952 8.10001C19.1072 8.2028 19.2911 8.371 19.4885 8.50767C19.4885 8.54621 19.4885 8.58359 19.4885 8.62331Z"
                  fill="#FF3465"
                />
                <path
                  d="M1.02436e-08 15.6114C0.660472 15.3019 1.3131 14.969 1.98478 14.6876C2.68674 14.3932 3.15546 13.8944 3.43693 13.1702C3.69932 12.4951 3.99648 11.8351 4.34297 11.0175C4.5235 11.3188 4.64236 11.473 4.71637 11.6482C5.96107 14.5835 5.43853 14.0006 8.26768 15.3428C8.44148 15.4257 8.6052 15.5308 8.94385 15.7201C8.41793 15.9665 8.0692 16.1838 7.6913 16.2994C6.28738 16.7293 5.38694 17.6497 4.98101 19.1157C4.87561 19.4941 4.6704 19.8422 4.51117 20.2055C4.4394 20.2474 4.36763 20.2895 4.29587 20.3316C4.25101 20.2066 4.20841 20.0793 4.15907 19.9555C4.103 19.8119 4.04245 19.6705 3.98189 19.5292C2.84822 16.8531 3.24629 17.3039 0.682898 16.1114C0.447416 16.0015 0.227632 15.8568 0 15.7282L1.02436e-08 15.6114Z"
                  fill="#9E77ED"
                />
              </svg>
            </div>
            <span className="text-sm font-medium text-[#000000]">
              Kroolo AI
            </span>
            {isTyping && (
              <div className="flex items-center gap-1">
                <div className="flex space-x-1">
                  <div className="h-1 w-1 animate-bounce rounded-full bg-gray-400"></div>
                  <div
                    className="h-1 w-1 animate-bounce rounded-full bg-gray-400"
                    style={{ animationDelay: "0.1s" }}
                  ></div>
                  <div
                    className="h-1 w-1 animate-bounce rounded-full bg-gray-400"
                    style={{ animationDelay: "0.2s" }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-[#000000]">You</span>
          </div>
        )}
      </div>

      <div
        className={`flex flex-col gap-2 rounded-lg border border-dashed border-[#97aeff] p-3 ${
          message.sender === "ai" ? "rounded-tl-sm" : "ml-auto rounded-tr-sm"
        }`}
      >
        <div className="whitespace-pre-wrap break-words text-sm text-[#202020]">
          <ReactMarkdown>{displayedContent}</ReactMarkdown>
        </div>
      </div>
      {(!isTyping || message.sender === "user") && (
        <div
          className={`flex items-center gap-0.5 ${
            message.sender === "user" ? "justify-end" : "justify-start"
          }`}
        >
          {message.sender === "ai" && (
            <>
              <ActionButton
                icon={copySuccess ? <Check size={16} /> : <Copy size={16} />}
                onClick={handleCopy}
                title={copySuccess ? "Copied!" : "Copy content"}
                className={copySuccess ? "text-green-600" : ""}
              />
              {isLastMessage && (
                <ActionButton
                  icon={<RefreshCw size={16} />}
                  onClick={onRegenerate}
                  title="Regenerate response"
                />
              )}
              <div className="ml-2 flex items-center gap-1">
                <ActionButton
                  icon={<ThumbsUp size={16} />}
                  onClick={() => handleFeedback("up")}
                  title="Helpful"
                  className={feedback === "up" ? "text-green-600" : ""}
                />
                <ActionButton
                  icon={<ThumbsDown size={16} />}
                  onClick={() => handleFeedback("down")}
                  title="Not helpful"
                  className={feedback === "down" ? "text-red-600" : ""}
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatMessage;
